/*
 * cart-fetch.js — shared /cart.js helpers (B2C-239, decisions Q5b/Q3)
 *
 * Single definition of "aggregate variant quantity" so the product-card
 * reconciliation (and any future consumer) never drifts on how multiple
 * cart lines of one variant are summed. Gelato boxes produce N lines per
 * variant (one per flavour combo); the badge/stepper must show the SUM.
 *
 * window.CasinettoCart.buildVariantMap(cart) ->
 *   { "<variantId>": { qty: SUM across lines, lineCount, key: (lineCount===1 ? key : null) } }
 * Correct for non-gelato too (lineCount always 1 -> key is its line key, qty its quantity).
 *
 * window.CasinettoCart.buildLineKey(variantIdOrGid, attributes) -> '<numericId>|<canonical attribute set>'
 * window.CasinettoCart.variantId(variantIdOrGid) -> '<numericId>'
 *   Single home of the per-line dedup key. merchandiseId + every line attribute
 *   EXCEPT the analytics ones (see ANALYTICS_KEYS) -- close to Shopify's own line
 *   identity, which is merchandise + the complete set, but deliberately narrower
 *   so that a line's provenance metadata cannot split one product into two.
 *   Accepts a numeric variant_id (/cart.js shape) or a
 *   gid://shopify/ProductVariant/<id> GID (Storefront API shape), and for the
 *   attributes either a /cart.js properties object or a Storefront attributes
 *   array.
 */
(function () {
  'use strict';

  var TIMEOUT_MS = 5000;

  function fetchCart() {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, signal: controller.signal })
      .then(function (r) { return r.json(); })
      .finally(function () { clearTimeout(timer); });
  }

  function buildVariantMap(cart) {
    var map = {};
    var items = (cart && cart.items) || [];
    items.forEach(function (item) {
      var v = String(item.variant_id);
      if (!map[v]) { map[v] = { qty: 0, lineCount: 0, key: null }; }
      map[v].qty += item.quantity;
      map[v].lineCount += 1;
      map[v].key = (map[v].lineCount === 1) ? item.key : null;
    });
    return map;
  }

  function variantId(variantIdOrGid) {
    var id;
    if (variantIdOrGid == null) {
      id = '';
    } else {
      id = String(variantIdOrGid);
    }
    var slash = id.lastIndexOf('/');
    if (slash !== -1) { id = id.slice(slash + 1); }
    var query = id.indexOf('?');
    if (query !== -1) { id = id.slice(0, query); }
    return id;
  }

  // Line attributes that describe HOW THE SHOPPER GOT HERE, not what they are
  // buying, and therefore take no part in line identity.
  //
  // The Flutter app writes all three on every cart line
  // (`cart_model_shopify.dart:754-761`), and `__ga_list_index` is a POSITION in
  // a list — so without this exclusion the same product added in the app and on
  // the web are two different lines forever, and the same product added from two
  // positions could be too. Measured on staging 2026-09-03: an app-style line
  // and a web-style line for one variant stayed two lines with quantities
  // unsummed.
  //
  // The product rule this serves, from Matteo and now in the contract: **never
  // two lines for the same product, except gelato, where each distinct set of
  // Flavours is its own line.** Analytics metadata living in an identity field
  // is what broke that rule, and excluding it by name is how the code says so.
  //
  // Excluded BY NAME rather than by an `__` prefix on purpose: `__shopify_*`
  // attributes are Shopify's own and may well bear identity.
  var ANALYTICS_KEYS = ['__ga_list_name', '__ga_list_id', '__ga_list_index'];

  function isAnalyticsKey(key) {
    for (var i = 0; i < ANALYTICS_KEYS.length; i++) {
      if (ANALYTICS_KEYS[i] === key) { return true; }
    }
    return false;
  }

  // Shopify's own line identity is merchandise + the COMPLETE attribute set,
  // not merchandise + Flavours. Measured on the dev store 2026-09-02: the same
  // variant added with IDENTICAL attributes merges into the existing line and
  // sums; with a different Flavours it stays a separate line; with NO
  // attributes it also stays separate -- so "no attributes" is an identity of
  // its own, distinct from any non-empty set.
  //
  // Keying on Flavours alone conflated any two lines differing only in some
  // other property, and the live case is in this theme: Horizon's
  // snippets/gift-card-recipient-form.liquid (rendered by blocks/buy-buttons)
  // posts Recipient email, Recipient name, Message, Send on and
  // __shopify_offset, so two gift cards bought for different people shared one
  // key. On a merge that summed a remote quantity into whichever of the two the
  // map happened to keep.
  //
  // Every attribute is included deliberately, __shopify_* included. Leaving out
  // one that DOES participate conflates two distinct lines, which is the bug
  // above; including one that does NOT participate only over-separates, and its
  // worst case is a duplicated line rather than a destroyed one. Safer
  // direction until something is measured to the contrary.
  function canonicalAttributes(attributes) {
    var pairs = [];
    var i;
    if (attributes == null) {
      pairs = [];
    } else if (typeof attributes === 'string') {
      // Legacy call shape: a bare Flavours value. Empty must mean "no
      // attributes", which is NOT the same identity as Flavours=''.
      if (attributes) { pairs.push(['Flavours', attributes]); }
    } else if (Object.prototype.toString.call(attributes) === '[object Array]') {
      // Storefront API shape: [{ key, value }]
      for (i = 0; i < attributes.length; i++) {
        if (attributes[i] && attributes[i].key != null &&
            !isAnalyticsKey(String(attributes[i].key))) {
          pairs.push([
            String(attributes[i].key),
            attributes[i].value == null ? '' : String(attributes[i].value)
          ]);
        }
      }
    } else if (typeof attributes === 'object') {
      // /cart.js shape: { 'Recipient email': '...', Flavours: '...' }
      var keys = Object.keys(attributes);
      for (i = 0; i < keys.length; i++) {
        if (isAnalyticsKey(keys[i])) { continue; }
        pairs.push([
          keys[i],
          attributes[keys[i]] == null ? '' : String(attributes[keys[i]])
        ]);
      }
    }
    // Order-insensitive, and it has to be: /cart.js hands the set over as an
    // OBJECT (insertion-ordered) while the Storefront API hands it over as an
    // ARRAY in its own order, so the same set arrives here in two different
    // orders. Sorting in encounter order instead would give identical lines
    // different keys roughly as often as matching ones — worse than the
    // conflation this function was rewritten to fix, because it is not rare.
    //
    // Sorted by key AND then value: two entries sharing a key cannot occur in
    // the object shape, but nothing stops the Storefront array from carrying
    // them, and key-only sorting would leave their relative order to the input.
    pairs.sort(function (a, b) {
      if (a[0] < b[0]) { return -1; }
      if (a[0] > b[0]) { return 1; }
      if (a[1] < b[1]) { return -1; }
      if (a[1] > b[1]) { return 1; }
      return 0;
    });
    // Percent-encoded so a key or value containing '=' or '&' cannot forge a
    // different pairing and collide with a genuinely different set.
    var out = [];
    for (i = 0; i < pairs.length; i++) {
      out.push(encodeURIComponent(pairs[i][0]) + '=' + encodeURIComponent(pairs[i][1]));
    }
    return out.join('&');
  }

  function buildLineKey(variantIdOrGid, attributes) {
    return variantId(variantIdOrGid) + '|' + canonicalAttributes(attributes);
  }

  // The canonical form of the COMPLETE attribute set, analytics keys included.
  //
  // Not an identity — buildLineKey is. This answers a different question: "does
  // this line carry exactly the attributes that one does?" Reconciliation needs
  // it, because two lines can share an identity and still differ: the app's line
  // carries GA attributes and the web's does not, and they are the same product.
  // Making the working copy EQUAL to the account's cart means converging those
  // too, not just the quantity — otherwise the mirror sends an attribute set the
  // service does not recognise and a duplicate line appears in the account's
  // cart, generated by the reconciliation meant to prevent exactly that.
  function attributesFingerprint(attributes) {
    var pairs = [];
    var i;
    if (attributes == null) {
      pairs = [];
    } else if (typeof attributes === 'string') {
      if (attributes) { pairs.push(['Flavours', attributes]); }
    } else if (Object.prototype.toString.call(attributes) === '[object Array]') {
      for (i = 0; i < attributes.length; i++) {
        if (attributes[i] && attributes[i].key != null) {
          pairs.push([
            String(attributes[i].key),
            attributes[i].value == null ? '' : String(attributes[i].value)
          ]);
        }
      }
    } else if (typeof attributes === 'object') {
      var keys = Object.keys(attributes);
      for (i = 0; i < keys.length; i++) {
        pairs.push([
          keys[i],
          attributes[keys[i]] == null ? '' : String(attributes[keys[i]])
        ]);
      }
    }
    pairs.sort(function (a, b) {
      if (a[0] < b[0]) { return -1; }
      if (a[0] > b[0]) { return 1; }
      if (a[1] < b[1]) { return -1; }
      if (a[1] > b[1]) { return 1; }
      return 0;
    });
    var out = [];
    for (i = 0; i < pairs.length; i++) {
      out.push(encodeURIComponent(pairs[i][0]) + '=' + encodeURIComponent(pairs[i][1]));
    }
    return out.join('&');
  }

  /* =======================================================================
   * QUANTITY CEILINGS — the cart-mutation wrapper (B2C-284)
   * =======================================================================
   *
   * CasinettoCart.add()    and CasinettoCart.change() are THE cart-mutation
   * path. Both ceilings live inside them, so a surface built next year cannot
   * forget the check — there is no other path left. That is the whole argument
   * for a wrapper over a module every surface opts into: the gelato gate was a
   * module, and every custom add surface built after it bypassed it.
   *
   * This is FEEDBACK, not enforcement. The cap is bypassable by anyone who sets
   * out to bypass it; Shopify still enforces stock server-side. See
   * decisions/spec-quantity-ceilings.md §1.
   *
   * The resolution rule lives HERE and not in a quantity-ceilings.js, on
   * purpose: a second file would be a second importable ceiling API, i.e. a
   * second mutation path (decisions/quantity-ceilings-structure.md D7).
   *
   *   add({ id, quantity, properties, productId, cap, stock,
   *         orderLimitMessage, silent })
   *     -> Promise<{ ok, cart, ceiling, item }>
   *   addMany([{ id, quantity, properties, productId, cap, stock }], opts)
   *     -> Promise<{ ok, cart, ceilings, items }>
   *   change({ key, quantity, productId, cap, stock,
   *            orderLimitMessage, silent })
   *     -> Promise<{ ok, cart, ceiling, item }>
   *
   * `ceiling` is null, or { reason, limit } with reason one of
   * ORDER_LIMIT · STOCK_BLOCKED · STOCK_CLAMPED · SOLD_OUT_ADD · SOLD_OUT_REMOVED.
   * `item` is /cart/add.js's line-item response, present only on a successful add.
   *
   * **add() IS addMany() with one item** — same engine, same ledgers, same tie
   * rule, one resolver (D14 §2). Two implementations of the resolution rule is
   * how a surface ends up able to pick the weaker one without anybody choosing
   * to build a bypass, so there is exactly one. The single difference between
   * the two forms is the fill policy, and it is the spec's: a single add refuses
   * WHOLE (spec §6, "refuse locally, no mutation is sent"), a bulk add fills
   * PARTIALLY (spec §7, "everything that can be added is added").
   *
   * `ceilings` is index-aligned to the items array handed in, null where an item
   * went through untouched — the caller pairs each ceiling with its own product
   * title, which lives at the call site and never in here. `items` is the raw
   * /cart/add.js response and is NOT aligned to the request. Never index one
   * against the other.
   *
   * The module fires the toast on the single-item calls, not the call site —
   * same reasoning as the wrapper itself. **addMany() never fires one and takes
   * no `silent`.** A vector cannot produce a sensible single message, so the bulk
   * caller composes one aggregated toast from the ceilings it gets back. That is
   * structural rather than a default because a flag that must be passed can be
   * forgotten, and forgetting it here means a dozen stacked toasts from one
   * press (D14 §4).
   */

  var REASON = {
    ORDER_LIMIT: 'ORDER_LIMIT',
    STOCK_BLOCKED: 'STOCK_BLOCKED',
    STOCK_CLAMPED: 'STOCK_CLAMPED',
    SOLD_OUT_ADD: 'SOLD_OUT_ADD',
    SOLD_OUT_REMOVED: 'SOLD_OUT_REMOVED'
  };

  /* The last cart this module saw. Used as the pre-flight map for products with
     NO cap, so the common path costs no extra round trip. A capped product
     always refetches instead — that refetch is the stale-tab fix, and it is
     affordable precisely because only 1.2% of products carry a cap. */
  var cachedCart = null;
  var cachedAt = 0;

  /* How long a just-fetched cart still counts as authoritative. It exists so a
     stepper press and the mutation its 300ms debounce sends share ONE /cart.js
     instead of two: the press runs the pre-flight, the debounced change() finds
     the answer still warm. Longer than the debounce, far shorter than a tab
     sitting idle — which is the case the refetch exists to catch. */
  var FRESH_MS = 1000;

  function remember(cart) {
    if (cart && cart.items) { cachedCart = cart; cachedAt = Date.now(); }
    return cart;
  }

  function forget() { cachedCart = null; cachedAt = 0; }

  /* Every cart:update drops the snapshot. Without this the freshness window is
     unsound: a surface that still writes the cart directly — gelato, recipe
     Add-All and buy-again until phase 2 migrates them, plus cart-sync
     reconciliation — leaves a snapshot that is wrong but still inside
     FRESH_MS, and the next capped press computes headroom from a cart that no
     longer exists.

     No source is exempt, deliberately. This module never emits cart:update —
     its callers do, after the mutation has already resolved — so there is no
     such thing as "our own" event to skip, and a source allowlist here would
     be dead code guarding an event nothing fires. Dropping unconditionally
     costs at most one refetch per completed mutation, and by then check() and
     the debounced change() have already shared their round trip, which is the
     only thing FRESH_MS exists to buy. */
  document.addEventListener('cart:update', forget);

  function fetchWithTimeout(url, opts) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
    opts = opts || {};
    opts.signal = controller.signal;
    return fetch(url, opts).finally(function () { clearTimeout(timer); });
  }

  /* Sibling of buildVariantMap, keyed on product_id and summed across lines.
     The cap is defined on the PRODUCT, so its headroom has to be summed at the
     product grain — which is also what makes gelato fall out correctly, where
     one variant occupies N lines (one per flavour combination). */
  function buildProductMap(cart) {
    var map = {};
    var items = (cart && cart.items) || [];
    items.forEach(function (item) {
      var p = String(item.product_id);
      if (!map[p]) { map[p] = { qty: 0, lineCount: 0 }; }
      map[p].qty += item.quantity;
      map[p].lineCount += 1;
    });
    return map;
  }

  /* ---- Normalisation, once, at the boundary ---------------------------- *
   * A number_integer metafield is a real integer in Liquid and becomes a
   * string only when it crosses into a data attribute. It is parsed here and
   * nowhere else, and never compared loosely. The two rules differ, and the
   * difference is the contract:
   *   cap   — missing, null, 0 and negative ALL mean "no cap".
   *   stock — ABSENT means "no stock ceiling known"; 0 is a real value that
   *           means zero, and a negative (oversold) reads as 0.
   */
  function normaliseCap(value) {
    var n = parseInt(value, 10);
    if (isNaN(n) || n <= 0) { return null; }
    return n;
  }

  function normaliseStock(value) {
    if (value === null || value === undefined || value === '') { return null; }
    var n = parseInt(value, 10);
    if (isNaN(n)) { return null; }
    return n < 0 ? 0 : n;
  }

  function findLine(cart, key) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === key) { return items[i]; }
    }
    return null;
  }

  /* ---- Copy ------------------------------------------------------------ *
   * Store-wide stock templates ride the cart-fetch.js script tag; the refusal
   * arrives per call, already resolved in Liquid with the real integer so its
   * plural form is correct. No English lives in this file (CONSTITUTION §IV.13).
   */
  var copy = null;

  function messages() {
    if (!copy) {
      var tag = document.querySelector('script[data-cart-fetch]');
      copy = (tag && tag.dataset) || {};
    }
    return copy;
  }

  function fill(template, count) {
    if (!template) { return ''; }
    return template.split('__COUNT__').join(String(count));
  }

  var warnedMissingRefusal = false;

  function messageFor(ceiling, orderLimitMessage) {
    var m = messages();
    if (ceiling.reason === REASON.ORDER_LIMIT) { return orderLimitMessage || ''; }
    if (ceiling.reason === REASON.STOCK_BLOCKED) { return fill(m.msgStockBlocked, ceiling.limit); }
    if (ceiling.reason === REASON.STOCK_CLAMPED) { return fill(m.msgStockClamped, ceiling.limit); }
    if (ceiling.reason === REASON.SOLD_OUT_ADD) { return m.msgSoldOutAdd || ''; }
    if (ceiling.reason === REASON.SOLD_OUT_REMOVED) { return m.msgSoldOutRemoved || ''; }
    return '';
  }

  function announce(ceiling, opts) {
    if (!ceiling || opts.silent) { return; }
    var message = messageFor(ceiling, opts.orderLimitMessage);
    if (!message) {
      /* An empty toast is worse than none. A caller that resolves an
         ORDER_LIMIT without having passed the string gets the ceiling back and
         one warning — the string is per-product and only the surface has it. */
      if (ceiling.reason === REASON.ORDER_LIMIT && !warnedMissingRefusal) {
        warnedMissingRefusal = true;
        if (window.console && console.warn) {
          console.warn('CasinettoCart: ORDER_LIMIT resolved with no orderLimitMessage; no toast fired.');
        }
      }
      return;
    }
    if (!window.CasinettoToast) { return; }
    var critical = ceiling.reason === REASON.ORDER_LIMIT;
    window.CasinettoToast.show({
      variant: critical ? 'critical' : 'info',
      role: critical ? 'alert' : 'status',
      message: message
    });
  }

  /* ---- Pre-flight ------------------------------------------------------ *
   * The cap is per PRODUCT and summed across every line; stock is per VARIANT
   * and summed across every line. Whichever headroom is smaller binds.
   *
   * ON A TIE THE CAP WINS. "Only 2 left in stock" implies a restock will help;
   * with a cap of 2 it will not, so the customer who comes back after a restock
   * still cannot have 3. The cap is the constraint that survives.
   *
   * This comparison exists ONCE, here, and every path reaches it: the scalar
   * pre-flight, the vector allocator and check() all call this function
   * (D14 §3 invariant 7 — "if the comparison logic appears twice in the file,
   * this invariant is broken regardless of whether the two copies agree").
   * The two callers differ only in where the headroom came from: from the cart
   * for a single mutation, from the in-memory ledgers for a batch.
   *
   *   resolveHeadroom({ cap, stock, capHead, stockHead, requested, isAdd })
   *     -> { accepted, ceiling }
   *
   * `accepted` is how much of the request fits, which only the bulk path acts
   * on; `ceiling` is what to say about the rest.
   */
  function resolveHeadroom(params) {
    var capHead = params.capHead;
    var stockHead = params.stockHead;
    if (capHead === null && stockHead === null) {
      return { accepted: params.requested, ceiling: null };
    }

    var head, source;
    if (capHead !== null && stockHead !== null) {
      if (capHead <= stockHead) { head = capHead; source = 'cap'; }
      else { head = stockHead; source = 'stock'; }
    } else if (capHead !== null) {
      head = capHead; source = 'cap';
    } else {
      head = stockHead; source = 'stock';
    }

    if (params.requested <= head) { return { accepted: params.requested, ceiling: null }; }

    var accepted = head > 0 ? head : 0;

    if (source === 'cap') {
      return { accepted: accepted, ceiling: { reason: REASON.ORDER_LIMIT, limit: params.cap } };
    }
    if (params.stock === 0) {
      /* Only meaningful on an add: there is nothing to have. On a change the
         line already exists, so post-flight decides between a clamp and a
         removal rather than guessing here. */
      if (params.isAdd) {
        return { accepted: accepted, ceiling: { reason: REASON.SOLD_OUT_ADD, limit: 0 } };
      }
      return { accepted: accepted, ceiling: null };
    }
    return { accepted: accepted, ceiling: { reason: REASON.STOCK_BLOCKED, limit: params.stock } };
  }

  /* Headroom for ONE mutation, read straight off a cart. `excludeQty` is the
     quantity this mutation REPLACES: the line's current quantity on a change,
     zero on an add. */
  function resolveCeiling(cart, params) {
    var cap = params.cap;
    var stock = params.stock;
    if (cap === null && stock === null) { return null; }

    var productMap = buildProductMap(cart);
    var variantMap = buildVariantMap(cart);

    var productTotal = 0;
    if (params.productId != null && productMap[String(params.productId)]) {
      productTotal = productMap[String(params.productId)].qty;
    }
    var variantTotal = 0;
    if (params.variantId != null && variantMap[String(params.variantId)]) {
      variantTotal = variantMap[String(params.variantId)].qty;
    }

    return resolveHeadroom({
      cap: cap,
      stock: stock,
      capHead: cap === null ? null : cap - (productTotal - params.excludeQty),
      stockHead: stock === null ? null : stock - (variantTotal - params.excludeQty),
      requested: params.requested,
      isAdd: params.isAdd
    }).ceiling;
  }

  /* Refetch only when a cap is present. The refetch closes the stale-tab case
     the cap alone cannot: two tabs would otherwise each believe they are under
     it. For everything else the cached cart is enough, and an unknown cart is
     no reason to add a round trip — post-flight remains the authority. */
  function preflightCart(hasCap, fetchIfUncached) {
    if (hasCap) {
      if (cachedCart && (Date.now() - cachedAt) < FRESH_MS) {
        return Promise.resolve(cachedCart);
      }
      return fetchCart().then(remember);
    }
    if (cachedCart) { return Promise.resolve(cachedCart); }
    /* Only `change()` asks for this, and only when a stock ceiling is known.
       An ADD with no cap must issue no extra round trip — it is the hot path,
       it has no optimistic number to snap back, and post-flight catches the
       stock case anyway. A stepper press does have a number on screen, and
       killing that snap-back is the whole point of emitting `data-stock`. */
    if (fetchIfUncached) { return fetchCart().then(remember); }
    return Promise.resolve(null);
  }

  /* ---- check() — the pre-flight on its own ----------------------------- *
   * Read-only: it resolves the ceiling and fires the toast, and it NEVER
   * mutates. It exists because spec §6 (Timing) requires the pre-flight to run
   * ON THE PRESS while the mutation stays behind the stepper's 300ms debounce —
   * a refusal must be instant and the displayed quantity must never tick up and
   * fall back. A caller runs this first, and only paints the new number if it
   * resolves null.
   *
   * This is NOT a second mutation path (D7): there is nothing to mutate here,
   * and add()/change() still run their own pre-flight, so a caller that skips
   * check() loses only the no-flicker property, never the ceiling.
   *
   *   check({ mode: 'add' | 'change', id | key, quantity, productId, cap,
   *           stock, orderLimitMessage, silent }) -> Promise<ceiling | null>
   */
  function check(opts) {
    opts = opts || {};
    var cap = normaliseCap(opts.cap);
    var stock = normaliseStock(opts.stock);
    if (cap === null && stock === null) { return Promise.resolve(null); }

    var isAdd = opts.mode !== 'change';
    var requested = parseInt(opts.quantity, 10);
    if (isNaN(requested)) { requested = isAdd ? 1 : 0; }
    if (requested <= 0) { return Promise.resolve(null); }

    return preflightCart(cap !== null, true).then(function (cart) {
      if (!cart) { return null; }
      var line = isAdd ? null : findLine(cart, opts.key);
      var lineQty = line ? line.quantity : 0;
      if (!isAdd && requested <= lineQty) { return null; }

      var ceiling = resolveCeiling(cart, {
        cap: cap,
        stock: stock,
        productId: line ? line.product_id : opts.productId,
        variantId: line ? line.variant_id : variantId(opts.id),
        excludeQty: isAdd ? 0 : lineQty,
        requested: requested,
        isAdd: isAdd
      });
      if (ceiling) { announce(ceiling, opts); }
      return ceiling;
    });
  }

  /* ---- The add engine: one batch, one allocation (D14) ----------------- *
   * Every add on the storefront lands here — add() is this with one item.
   * The shape is fixed at O(1) requests in basket size:
   *
   *   2 when no requested product carries a cap   (POST + post-flight read)
   *   3 when one does                             (pre-flight /cart.js first)
   *   4-5 in the raced case                       (the one retry below)
   *
   * A request is normalised ONCE, here, at the boundary — the same rules the
   * scalar path always used (normaliseCap / normaliseStock).
   */
  function normaliseRequest(opts) {
    opts = opts || {};
    var quantity = parseInt(opts.quantity, 10);
    if (isNaN(quantity) || quantity < 1) { quantity = 1; }
    var id = variantId(opts.id);
    var numeric = parseInt(id, 10);
    var productId = null;
    if (opts.productId !== null && opts.productId !== undefined && opts.productId !== '') {
      productId = String(opts.productId);
    }
    return {
      variantId: id,
      numericId: isNaN(numeric) ? null : numeric,
      productId: productId,
      quantity: quantity,
      properties: opts.properties || null,
      cap: normaliseCap(opts.cap),
      stock: normaliseStock(opts.stock)
    };
  }

  /* TWO LEDGERS AT TWO GRAINS, because the two ceilings have two grains
     (spec §4): cap headroom keyed by product_id, stock headroom keyed by
     variant_id. One ledger keyed either way silently breaks the other ceiling —
     keyed by variant it breaks the per-product cap, keyed by product it breaks
     stock on a multi-variant product (D14 §3 invariant 2).

     BOTH ARE INITIALISED FROM THE SNAPSHOT, not from the raw cap or the raw
     stock: cap minus what the cart already holds of that product, stock minus
     what it already holds of that variant. An allocator seeded with the raw cap
     enforces "N per press" instead of "N per cart" — and passes every test run
     against an empty cart while being wrong for every shopper who already has
     one (invariant 3).

     Repeats within one request draw on ONE entry, which is the actual phase 2
     case rather than a hypothetical: two rows of the same product share the cap
     entry, and N gelato `Flavours` lines of one variant share the stock entry
     (invariant 8). The ledger is scoped to the call and dies with it — it is
     derived state, and a surviving ledger is a stale cap (invariant 9). */
  function buildLedgers(cart, requests) {
    var productMap = buildProductMap(cart);
    var variantMap = buildVariantMap(cart);
    var cap = {};
    var stock = {};
    requests.forEach(function (r) {
      /* The cap is a product-level metafield, so every request for one product
         declares the same number; the first one to arrive seeds the entry. */
      if (r.cap !== null && r.productId !== null && !(r.productId in cap)) {
        cap[r.productId] = r.cap - (productMap[r.productId] ? productMap[r.productId].qty : 0);
      }
      if (r.stock !== null && r.variantId && !(r.variantId in stock)) {
        stock[r.variantId] = r.stock - (variantMap[r.variantId] ? variantMap[r.variantId].qty : 0);
      }
    });
    return { cap: cap, stock: stock };
  }

  /* Allocate in REQUEST ORDER, never sorted (invariant 4): scarce headroom goes
     to the earlier item, so the outcome is reproducible and explicable ("the
     first two got in"). Sorting by anything makes it unexplainable to the
     shopper and untestable for us.

     `wanted` is index-aligned to `requests` — it is what we are trying to add
     on THIS pass, which is the original quantity on the first pass and the
     already-trimmed quantity on the retry. Returns an index-aligned array of
     { accepted, ceiling }. */
  function allocate(requests, wanted, ledgers, partial) {
    return requests.map(function (r, i) {
      var requested = wanted[i];
      if (requested <= 0) { return { accepted: 0, ceiling: null }; }

      var hasCapHead = r.cap !== null && r.productId !== null && (r.productId in ledgers.cap);
      var hasStockHead = r.stock !== null && r.variantId && (r.variantId in ledgers.stock);
      var capHead = hasCapHead ? ledgers.cap[r.productId] : null;
      var stockHead = hasStockHead ? ledgers.stock[r.variantId] : null;

      var fit = resolveHeadroom({
        cap: r.cap,
        stock: r.stock,
        capHead: capHead,
        stockHead: stockHead,
        requested: requested,
        isAdd: true
      });

      /* Partial fill is the bulk rule and nothing is ever silently reduced:
         an item asking for 3 with headroom 2 is added at 2 AND reported in
         `ceilings`; headroom 0 drops it and it is still reported (invariant 5).
         A single add refuses whole instead — spec §6, and the only difference
         between the two forms. */
      var accepted = partial ? fit.accepted : (fit.ceiling ? 0 : requested);

      if (accepted > 0) {
        /* Decrement by what was ACCEPTED, never by what was requested, and only
           on acceptance: a partially-filled item that debited the full request
           would eat headroom twice and wrongly block a later line
           (invariant 6). */
        if (hasCapHead) { ledgers.cap[r.productId] = capHead - accepted; }
        if (hasStockHead) { ledgers.stock[r.variantId] = stockHead - accepted; }
      }
      return { accepted: accepted, ceiling: fit.ceiling };
    });
  }

  function anyCeiling(ceilings) {
    for (var i = 0; i < ceilings.length; i++) {
      if (ceilings[i]) { return true; }
    }
    return false;
  }

  function batchResult(cart, ceilings, items) {
    return { ok: !anyCeiling(ceilings) && items.length > 0, cart: cart || null, ceilings: ceilings, items: items };
  }

  /* /cart/add.js answers a scalar body with the line itself and an `items` body
     with { items: [...] }. Everything posts the vector form now; this only
     guards the shape. NOT aligned to the request — see the header. */
  function responseItems(payload) {
    if (!payload) { return []; }
    if (Object.prototype.toString.call(payload.items) === '[object Array]') { return payload.items; }
    return [payload];
  }

  function postBatch(requests, quantities) {
    var lines = [];
    requests.forEach(function (r, i) {
      if (quantities[i] > 0) {
        var line = { id: r.numericId, quantity: quantities[i] };
        if (r.properties) { line.properties = r.properties; }
        lines.push(line);
      }
    });
    return fetchWithTimeout('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: lines })
    });
  }

  /* The batch was refused and our own state does not explain it. Derive each
     unexplained line's ceiling from the CART — the quantity already there IS
     the true maximum (spec §6 post-flight step 4).

     NEVER from Shopify's message prose. It is English from a third party, it
     has changed wording before, and the measurement on 2026-09-22 confirmed the
     422 body names the failing product in a sentence and nowhere else — parsing
     it to decide what to drop is the trap spec §6 already forbids. */
  function terminalFromCart(requests, attempted, ceilings, cart) {
    var map = buildVariantMap(cart);
    requests.forEach(function (r, i) {
      if (attempted[i] <= 0 || ceilings[i]) { return; }
      var entry = map[r.variantId];
      var have = entry ? entry.qty : 0;
      ceilings[i] = have > 0
        ? { reason: REASON.STOCK_BLOCKED, limit: have }
        : { reason: REASON.SOLD_OUT_ADD, limit: 0 };
    });
    return batchResult(cart, ceilings, []);
  }

  /* MEASURED on the dev store, 2026-09-22: POST /cart/add.js with an `items`
     array is ATOMIC. One in-stock line plus one sold-out line returns 422 and
     NOTHING lands, the good line included. So spec §7's "everything that can be
     added is added" is delivered by THIS retry, not by the batch call — without
     it one sold-out ingredient costs the shopper the entire recipe.

     A 422 after a clean pre-flight means stock moved between the snapshot and
     the POST. Re-read the cart, re-run the allocation from cart state, drop what
     is newly blocked, re-issue once.

     EXACTLY ONE retry, never a loop: a retry loop on a contended SKU is a
     request storm launched by one shopper's single press. */
  function retryOnce(requests, attempted, ceilings, partial) {
    return fetchCart().then(remember).then(function (fresh) {
      var ledgers = buildLedgers(fresh, requests);
      var fits = allocate(requests, attempted, ledgers, partial);
      var next = attempted.slice();
      var dropped = false;
      var survives = false;

      fits.forEach(function (fit, i) {
        if (attempted[i] <= 0) { return; }
        next[i] = fit.accepted;
        if (fit.ceiling) { ceilings[i] = fit.ceiling; }
        if (fit.accepted < attempted[i]) { dropped = true; }
        if (fit.accepted > 0) { survives = true; }
      });

      /* Re-issue only if the re-allocation actually dropped something. An
         identical body earns an identical 422, so re-sending it salvages
         nothing — and on the single-item form, where there is no "rest" to
         salvage, it would add a doomed POST to every refused add on the site. */
      if (!dropped || !survives) {
        return terminalFromCart(requests, attempted, ceilings, fresh);
      }

      return postBatch(requests, next).then(function (res) {
        if (res.status === 422) {
          /* The retry also failed. Stop — no loop. Nothing landed, so the read
             from a moment ago is still the best state we have. */
          return terminalFromCart(requests, next, ceilings, fresh);
        }
        if (!res.ok) { throw new Error(res.status); }
        return res.json().then(function (payload) {
          return fetchCart().then(remember).then(function (after) {
            return batchResult(after, ceilings, responseItems(payload));
          });
        });
      });
    });
  }

  function sendAllocated(requests, accepted, ceilings, cart, partial) {
    var any = false;
    for (var i = 0; i < accepted.length; i++) {
      if (accepted[i] > 0) { any = true; break; }
    }
    /* Nothing survived the allocation: the refusal is complete and NO mutation
       is sent (spec §6). */
    if (!any) { return Promise.resolve(batchResult(cart, ceilings, [])); }

    return postBatch(requests, accepted).then(function (res) {
      if (res.status === 422) { return retryOnce(requests, accepted, ceilings, partial); }
      if (!res.ok) { throw new Error(res.status); }
      return res.json().then(function (payload) {
        return fetchCart().then(remember).then(function (fresh) {
          return batchResult(fresh, ceilings, responseItems(payload));
        });
      });
    });
  }

  function addBatch(requests, partial) {
    var wanted = requests.map(function (r) {
      return r.numericId === null ? 0 : r.quantity;
    });
    var ceilings = requests.map(function () { return null; });
    var hasCap = false;
    requests.forEach(function (r) { if (r.cap !== null) { hasCap = true; } });

    /* ONE snapshot for the whole call, under exactly the D10 rules the scalar
       path has always used: fetched when any requested product carries a cap,
       the cached one otherwise, and never a second read mid-allocation
       (invariant 1). With no cart in hand nothing is resolved and everything
       goes out as asked — post-flight remains the authority. */
    return preflightCart(hasCap).then(function (cart) {
      var accepted = wanted.slice();
      if (cart) {
        var fits = allocate(requests, wanted, buildLedgers(cart, requests), partial);
        fits.forEach(function (fit, i) {
          accepted[i] = fit.accepted;
          ceilings[i] = fit.ceiling;
        });
      }
      return sendAllocated(requests, accepted, ceilings, cart, partial);
    });
  }

  /* addMany — the bulk form. One pre-flight, one cumulative allocation, one
     POST, one post-flight read, whatever the basket size. It NEVER fires a
     toast and takes no `silent` (D14 §4): the caller composes one aggregated
     message from the index-aligned `ceilings`.

     `opts` is reserved; nothing is read from it today, and `silent` will never
     be. A caller that ignores a non-empty `ceilings` array produces exactly the
     silent failure this feature exists to kill — the module cannot detect that,
     so it is a documented caller obligation. */
  function addMany(items, opts) {
    var list = (items || []).map(normaliseRequest);
    if (!list.length) {
      return Promise.resolve({ ok: true, cart: null, ceilings: [], items: [] });
    }
    return addBatch(list, true);
  }

  /* add — the single-item form of addMany, not a second implementation
     (D14 §2). Same signature and same return shape as it has always had: the
     module fires the toast here (honouring `silent`), the ceiling is
     `ceilings[0]` and the new line is `items[0]`, which is the authoritative
     source of the line key (D11) and must not be re-derived at the call site. */
  function add(opts) {
    opts = opts || {};
    return addBatch([normaliseRequest(opts)], false).then(function (batch) {
      var ceiling = batch.ceilings[0] || null;
      if (ceiling) { announce(ceiling, opts); }
      return {
        ok: batch.ok,
        cart: batch.cart,
        ceiling: ceiling,
        item: batch.items[0] || null
      };
    });
  }

  function change(opts) {
    opts = opts || {};
    var quantity = parseInt(opts.quantity, 10);
    if (isNaN(quantity) || quantity < 0) { quantity = 0; }
    var cap = normaliseCap(opts.cap);
    var stock = normaliseStock(opts.stock);
    var key = opts.key;

    /* A decrease can never breach a ceiling, so it skips the pre-flight
       entirely — including the capped product's extra round trip. */
    var needsPreflight = quantity > 0 && (cap !== null || stock !== null);

    return (needsPreflight ? preflightCart(cap !== null, stock !== null) : Promise.resolve(null))
      .then(function (cart) {
        if (cart) {
          var line = findLine(cart, key);
          var lineQty = line ? line.quantity : 0;
          if (quantity > lineQty) {
            var ceiling = resolveCeiling(cart, {
              cap: cap,
              stock: stock,
              productId: line ? line.product_id : opts.productId,
              variantId: line ? line.variant_id : opts.variantId,
              excludeQty: lineQty,
              requested: quantity,
              isAdd: false
            });
            if (ceiling) {
              announce(ceiling, opts);
              return { ok: false, cart: cart, ceiling: ceiling, item: null };
            }
          }
        }

        return fetchWithTimeout('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        }).then(function (res) {
          if (!res.ok) { throw new Error(res.status); }
          return res.json();
        }).then(function (fresh) {
          remember(fresh);

          /* /cart/change.js returns 200 EVEN WHEN IT CLAMPS. Compare what was
             asked for against the line that came back. A requested 0 removes
             the line on purpose and is not a ceiling. */
          var ceiling = null;
          if (quantity > 0) {
            var line = findLine(fresh, key);
            if (!line) {
              ceiling = { reason: REASON.SOLD_OUT_REMOVED, limit: 0 };
            } else if (line.quantity < quantity) {
              ceiling = { reason: REASON.STOCK_CLAMPED, limit: line.quantity };
            }
          }
          announce(ceiling, opts);
          return { ok: !ceiling, cart: fresh, ceiling: ceiling, item: null };
        });
      });
  }

  window.CasinettoCart = {
    fetchCart: fetchCart,
    buildVariantMap: buildVariantMap,
    buildProductMap: buildProductMap,
    buildLineKey: buildLineKey,
    attributesFingerprint: attributesFingerprint,
    variantId: variantId,
    check: check,
    add: add,
    addMany: addMany,
    change: change
  };
})();
